// What a WhatsApp connection is actually worth, read off a `businesses` row.
//
// Stored Meta credentials are not the same thing as a working number. Meta
// only delivers inbound messages once the number has been registered for Cloud
// API, and Embedded Signup does not do that, so a business can hold a
// complete, valid set of credentials while Meta keeps the number PENDING and
// the AI never receives anything. The backend writes whatsapp_status
// 'connected' only when Meta itself reports status=CONNECTED, which is why
// that column (not meta_connected_at) decides whether we say "connected".
//
// Shared by the dashboard card and the connected-channel gate so the two can
// never disagree about whether WhatsApp works.

export interface WhatsAppConnectionFields {
  whatsapp_provider?: string | null;
  whatsapp_status?: string | null;
  whatsapp_number?: string | null;
  meta_connected_at?: string | null;
  meta_phone_number_id?: string | null;
}

export type MetaWhatsAppState =
  /** Meta reports the number CONNECTED; inbound reaches Dibs. */
  | "connected"
  /** Credentials stored and webhook wired, but Meta hasn't made it live. */
  | "pending_registration"
  /** No Meta connection on this business. */
  | "not_connected";

/** True when Meta credentials are stored, regardless of whether Meta has
 * activated the number. Enough to offer a repair or a disconnect. */
export function hasMetaWhatsAppCredentials(
  row: WhatsAppConnectionFields | null | undefined,
): boolean {
  return row?.whatsapp_provider === "meta_cloud" &&
    !!row?.meta_connected_at &&
    !!row?.meta_phone_number_id;
}

export function metaWhatsAppState(
  row: WhatsAppConnectionFields | null | undefined,
): MetaWhatsAppState {
  if (!hasMetaWhatsAppCredentials(row)) return "not_connected";
  return row?.whatsapp_status === "connected"
    ? "connected"
    : "pending_registration";
}

/** A historical non-Meta WhatsApp connection that may still route server-side
 * during migration. */
export function hasLegacyWhatsAppConnection(
  row: WhatsAppConnectionFields | null | undefined,
): boolean {
  return row?.whatsapp_provider !== "meta_cloud" &&
    row?.whatsapp_status === "connected" &&
    !!row?.whatsapp_number;
}

/** Whether WhatsApp counts as a working messaging channel for this business.
 *
 * Any row whose own status says connected and that has a number keeps
 * counting, whatever the provider label says, that is what has always kept
 * historical senders routing through the migration. What no longer counts is
 * a Meta row that has credentials but no activated number. */
export function whatsappChannelConnected(
  row: WhatsAppConnectionFields | null | undefined,
): boolean {
  return metaWhatsAppState(row) === "connected" ||
    (row?.whatsapp_status === "connected" && !!row?.whatsapp_number);
}
